import { useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogOut, RefreshCw, Trash2, Mail, Phone, Search } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const STORAGE_KEY = "ig_admin_pw";

function formatDate(iso) {
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function LoginGate({ onSuccess }) {
  const [pw, setPw] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!pw) return;
    try {
      setLoading(true);
      await axios.post(`${API}/admin/login`, { password: pw });
      sessionStorage.setItem(STORAGE_KEY, pw);
      onSuccess(pw);
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) toast.error("Incorrect password.");
      else if (status === 503)
        toast.error("Admin access is not configured on the server.");
      else toast.error("Unable to verify password. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="admin-login"
      className="min-h-screen flex items-center justify-center bg-[#F9F8F6] px-6"
    >
      <form
        onSubmit={submit}
        className="w-full max-w-md bg-[#F9F8F6] border border-[#E5E1DB] rounded-sm p-10"
      >
        <p className="text-[10px] tracking-[0.4em] uppercase text-[#5C6658] mb-3">
          Indoor Greens · Studio
        </p>
        <h1 className="font-[Outfit] font-extralight text-[36px] text-[#2D332B] tracking-tight">
          Admin access
        </h1>
        <p className="mt-3 text-[14px] text-[#5C6658]">
          Enter the admin password to view enquiries.
        </p>

        <div className="mt-8">
          <Label
            htmlFor="admin-pw"
            className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]"
          >
            Password
          </Label>
          <Input
            id="admin-pw"
            data-testid="admin-password-input"
            type="password"
            autoFocus
            value={pw}
            onChange={(e) => setPw(e.target.value)}
            className="mt-2 bg-transparent border-0 border-b border-[#E5E1DB] rounded-none focus-visible:ring-0 focus-visible:border-[#8A9A86] px-0 h-11 text-[#2D332B]"
          />
        </div>

        <button
          type="submit"
          disabled={loading}
          data-testid="admin-login-button"
          className="mt-10 w-full inline-flex items-center justify-center px-7 py-3.5 bg-[#2D332B] text-[#F9F8F6] text-[11px] tracking-[0.3em] uppercase hover:bg-[#8A9A86] disabled:opacity-60 transition-colors duration-500 rounded-sm"
        >
          {loading ? "Verifying…" : "Sign in"}
        </button>

        <a
          href="/"
          data-testid="admin-back-home"
          className="mt-6 block text-center text-[11px] tracking-[0.25em] uppercase text-[#5C6658] hover:text-[#8A9A86]"
        >
          ← Back to site
        </a>
      </form>
    </div>
  );
}

function InquiryRow({ inq, onDelete, expanded, onToggle }) {
  return (
    <div
      data-testid={`inquiry-row-${inq.id}`}
      className="bg-[#F9F8F6] border border-[#E5E1DB] rounded-sm"
    >
      <button
        onClick={onToggle}
        data-testid={`inquiry-toggle-${inq.id}`}
        className="w-full text-left grid grid-cols-1 sm:grid-cols-12 gap-3 sm:gap-6 px-5 sm:px-7 py-5 items-center hover:bg-[#F2EFEB] transition-colors rounded-sm"
      >
        <div className="sm:col-span-3">
          <p className="font-[Outfit] text-[17px] text-[#2D332B]">
            {inq.name}
          </p>
          <p className="text-[11px] tracking-[0.2em] uppercase text-[#5C6658] mt-1">
            {inq.project_type || "—"}
          </p>
        </div>
        <div className="sm:col-span-4 text-[13px] text-[#2D332B] break-all">
          {inq.email}
          {inq.phone && (
            <span className="block text-[12px] text-[#5C6658] mt-1">
              {inq.phone}
            </span>
          )}
        </div>
        <div className="sm:col-span-3 text-[12px] text-[#5C6658] line-clamp-2 sm:line-clamp-1">
          {inq.message}
        </div>
        <div className="sm:col-span-2 text-right text-[11px] tracking-[0.15em] uppercase text-[#5C6658]">
          {formatDate(inq.created_at)}
        </div>
      </button>

      {expanded && (
        <div
          data-testid={`inquiry-detail-${inq.id}`}
          className="border-t border-[#E5E1DB] px-5 sm:px-7 py-6 bg-[#F2EFEB]"
        >
          <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]">
            Message
          </p>
          <p className="mt-3 text-[15px] leading-relaxed text-[#2D332B] whitespace-pre-wrap">
            {inq.message}
          </p>

          <div className="mt-6 flex flex-wrap items-center gap-4">
            <a
              href={`mailto:${inq.email}`}
              className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.25em] uppercase border border-[#2D332B] text-[#2D332B] hover:bg-[#2D332B] hover:text-[#F9F8F6] rounded-sm transition-colors"
            >
              <Mail className="w-3.5 h-3.5" /> Reply
            </a>
            {inq.phone && (
              <a
                href={`tel:${inq.phone}`}
                className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.25em] uppercase border border-[#E5E1DB] text-[#2D332B] hover:bg-[#F9F8F6] rounded-sm transition-colors"
              >
                <Phone className="w-3.5 h-3.5" /> Call
              </a>
            )}
            <button
              onClick={() => onDelete(inq.id)}
              data-testid={`inquiry-delete-${inq.id}`}
              className="ml-auto inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.25em] uppercase text-[#8A0000] hover:text-[#5a0000] rounded-sm transition-colors"
            >
              <Trash2 className="w-3.5 h-3.5" /> Delete
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function AdminDashboard({ password, onLogout }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/contact`, {
        headers: { "X-Admin-Password": password },
      });
      setItems(Array.isArray(data) ? data : []);
    } catch (err) {
      if (err?.response?.status === 401) {
        sessionStorage.removeItem(STORAGE_KEY);
        onLogout();
        toast.error("Session expired. Please sign in again.");
      } else {
        toast.error("Could not load inquiries.");
      }
    } finally {
      setLoading(false);
    }
  }, [password, onLogout]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) =>
      [i.name, i.email, i.phone, i.project_type, i.message]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [items, query]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this inquiry permanently?")) return;
    try {
      await axios.delete(`${API}/admin/contact/${id}`, {
        headers: { "X-Admin-Password": password },
      });
      setItems((arr) => arr.filter((i) => i.id !== id));
      toast.success("Inquiry deleted.");
    } catch {
      toast.error("Could not delete.");
    }
  };

  return (
    <div className="min-h-screen bg-[#F2EFEB]" data-testid="admin-dashboard">
      <header className="bg-[#F9F8F6] border-b border-[#E5E1DB]">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 h-20 flex items-center justify-between">
          <div>
            <p className="text-[10px] tracking-[0.4em] uppercase text-[#5C6658]">
              Indoor Greens · Studio
            </p>
            <h1 className="font-[Outfit] font-light text-[22px] text-[#2D332B]">
              Inquiries
            </h1>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={load}
              data-testid="admin-refresh"
              className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.25em] uppercase border border-[#E5E1DB] text-[#2D332B] hover:bg-[#F2EFEB] rounded-sm transition-colors"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
            <button
              onClick={onLogout}
              data-testid="admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 text-[11px] tracking-[0.25em] uppercase border border-[#2D332B] text-[#2D332B] hover:bg-[#2D332B] hover:text-[#F9F8F6] rounded-sm transition-colors"
            >
              <LogOut className="w-3.5 h-3.5" /> Sign out
            </button>
          </div>
        </div>
      </header>

      <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16 py-10 sm:py-14">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-8">
          <div>
            <p className="text-[11px] tracking-[0.3em] uppercase text-[#5C6658]">
              {loading
                ? "Loading…"
                : `${filtered.length} ${
                    filtered.length === 1 ? "inquiry" : "inquiries"
                  }`}
            </p>
          </div>
          <div className="relative w-full sm:w-80">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5C6658]" />
            <Input
              data-testid="admin-search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by name, email, project…"
              className="pl-9 bg-[#F9F8F6] border-[#E5E1DB] focus-visible:ring-0 focus-visible:border-[#8A9A86] rounded-sm h-10 text-[14px]"
            />
          </div>
        </div>

        {loading ? (
          <div className="space-y-3">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-24 bg-[#F9F8F6] border border-[#E5E1DB] rounded-sm animate-pulse"
              />
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="bg-[#F9F8F6] border border-dashed border-[#E5E1DB] rounded-sm py-24 text-center">
            <p className="font-[Outfit] text-[24px] font-light text-[#2D332B]">
              {items.length === 0 ? "No inquiries yet." : "No matches."}
            </p>
            <p className="mt-2 text-[13px] text-[#5C6658]">
              {items.length === 0
                ? "When someone submits the contact form, it will appear here."
                : "Try a different search."}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filtered.map((inq) => (
              <InquiryRow
                key={inq.id}
                inq={inq}
                expanded={expanded === inq.id}
                onToggle={() =>
                  setExpanded((cur) => (cur === inq.id ? null : inq.id))
                }
                onDelete={handleDelete}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default function Admin() {
  const [password, setPassword] = useState(() =>
    sessionStorage.getItem(STORAGE_KEY) || ""
  );

  const handleLogout = () => {
    sessionStorage.removeItem(STORAGE_KEY);
    setPassword("");
  };

  if (!password) {
    return <LoginGate onSuccess={setPassword} />;
  }
  return <AdminDashboard password={password} onLogout={handleLogout} />;
}
